import {forwardRef, MouseEvent, TouchEvent, useCallback, useRef} from 'react';
import {useShareForwardedRef} from '../../../common/shared/hooks/useShareForwardedRef';
import {LongTapProps} from './LongTapProps.interface';

export const LongTap = forwardRef<HTMLDivElement, LongTapProps>(
  (
    {
      onLongTap,
      longTapTimer = 1500,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      children,
      isMobile,
      ...props
    }: LongTapProps,
    ref
  ) => {
    const innerRef = useShareForwardedRef(ref);
    const backgroundPressingTimer = useRef<number>(0);
    const isLongTapPressed = useRef<boolean>(false);
    const isAfterPressing = useRef<boolean>(false);
    const longTapEventTime = useRef(0);

    // LongPress 시 포인터가 이동하면 캔슬하기 위한 용도
    const startPos = useRef<{x: number; y: number}>({x: 0, y: 0});
    const currPos = useRef<{x: number; y: number}>({x: 0, y: 0});
    const handlePressing = useCallback(
      (e: TouchEvent | MouseEvent) => {
        e.stopPropagation();
        if ('touches' in e) {
          startPos.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
          };
          if (onTouchStart) {
            onTouchStart(e);
          }
        } else {
          startPos.current = {x: e.clientX, y: e.clientY};
          if (onMouseDown) {
            onMouseDown(e);
          }
        }
        isAfterPressing.current = true;
        isLongTapPressed.current = false;
        clearTimeout(backgroundPressingTimer.current);
        backgroundPressingTimer.current = window.setTimeout(() => {
          isLongTapPressed.current = true;
          if (onLongTap) {
            onLongTap();
          }
        }, longTapTimer);
      },
      [onLongTap, longTapTimer, onTouchStart, onMouseDown]
    );

    const handlePressingRelease = useCallback(
      (e: TouchEvent | MouseEvent) => {
        e.stopPropagation();
        if ('touches' in e) {
          if (onTouchEnd) {
            onTouchEnd(e);
          }
        } else {
          if (onMouseUp) {
            onMouseUp(e);
          }
        }
        if (isLongTapPressed.current) {
          // preventDefault로 touchEnd 이후에 오는 모든 이벤트를 실행시키지 않는다. 결과적으로 뒤에 따라오는 onClick이 실행되지 않음.
          e.preventDefault();
          // PC에서는 preventDefault가 뒤따라오는 onClick을 막지 못한다. 추후 모바일 기기에서도 그런 일이 일어날 가능성이 있으므로 timestamp를 사용한 방법도 추가해놓음.
          longTapEventTime.current = e.timeStamp;
        }
        isLongTapPressed.current = false;
        isAfterPressing.current = false;
        clearTimeout(backgroundPressingTimer.current);
      },
      [onMouseUp, onTouchEnd]
    );

    const handleMoving = useCallback(
      (e: TouchEvent | MouseEvent) => {
        if (!isAfterPressing.current) {
          return;
        }
        if ('touches' in e) {
          currPos.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
          };
          if (onTouchMove) {
            onTouchMove(e);
          }
        } else {
          currPos.current = {x: e.clientX, y: e.clientY};
          if (onMouseMove) {
            onMouseMove(e);
          }
        }
        if (
          Math.abs(startPos.current.x - currPos.current.x) < 10 &&
          Math.abs(startPos.current.y - currPos.current.y) < 10
        ) {
          return;
        }
        clearTimeout(backgroundPressingTimer.current);
        isAfterPressing.current = false;
      },
      [onTouchMove, onMouseMove]
    );

    const onClickCapture = useCallback((e: MouseEvent) => {
      if (
        longTapEventTime.current &&
        e.timeStamp - longTapEventTime.current <= TIME_CONSIDERD_SAME_EVENT
      ) {
        e.stopPropagation();
      }
    }, []);

    return (
      <div
        onTouchStart={isMobile ? handlePressing : undefined}
        onMouseDown={!isMobile ? handlePressing : undefined}
        onTouchMove={isMobile ? handleMoving : undefined}
        onMouseMove={!isMobile ? handleMoving : undefined}
        onTouchEnd={isMobile ? handlePressingRelease : undefined}
        onMouseUp={!isMobile ? handlePressingRelease : undefined}
        onClickCapture={onClickCapture}
        ref={innerRef}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// touchEnd(mouseUp)과 click 이벤트의 발생 시간이 이 수치보다 작으면 같은 동작에서 발생한 이벤트로 간주한다.
const TIME_CONSIDERD_SAME_EVENT = 100;
